import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import type {
  ListPublishedLegendRequest,
  PublishedLegendResource,
} from './resources/legend'
import { toURLSearchParams } from '@/utils/url-util'

const SUPPORTED_ENGINE_VERSION = '1.15.1'
const supportedEngineVersionParams = {
  supported_engine_version: SUPPORTED_ENGINE_VERSION,
} as const

export class LegendsApi {
  readonly publishedLegends

  constructor(options: ApiClientOptions) {
    this.publishedLegends = new PublishedLegendApi(options)
  }
}

export class PublishedLegendApi extends ApiClient {
  /**
   * Lists published legends.
   */
  list(
    { workspace_id: workspaceId, ...request }: ListPublishedLegendRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<PublishedLegendResource>> {
    return this._get(
      `v3/workspaces/${workspaceId}/legends`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  /**
   * Gets the latest version of a legend.
   *
   * @param id
   * @param signal
   */
  get(id: string, signal?: AbortSignal): Promise<PublishedLegendResource> {
    return this._get(`v3/legends/${encodeURIComponent(id)}`, undefined, {
      signal,
    })
  }

  /**
   * Gets a specific version of a legend.
   *
   * @param id
   * @param version
   * @param signal
   */
  getVersion(
    id: string,
    version: number,
    signal?: AbortSignal,
  ): Promise<PublishedLegendResource> {
    return this._get(
      `v3/legends/${encodeURIComponent(id)}/versions/${version}`,
      undefined,
      {
        signal,
      },
    )
  }

  /**
   * Gets a specific version of a legend, as served to the engine version this
   * app supports.
   *
   * @param id
   * @param version
   * @param signal
   */
  getSupportedVersion(
    id: string,
    version: number,
    signal?: AbortSignal,
  ): Promise<PublishedLegendResource> {
    return this._get(
      `v3/legends/${encodeURIComponent(id)}/versions/${version}`,
      toURLSearchParams(supportedEngineVersionParams),
      { signal },
    )
  }
}
